import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/Card';
import { FormNavigation } from '../components/form/FormNavigation';
import { RadioGroup } from '../components/fields/RadioGroup';
import { useFormStore, useNavigation } from '../store/formStore';
import { durationStepSchema } from '../utils/validation';
import { getAvailableDurations } from '../config/loanDurations';

interface DurationFormData {
  duration: string;
  demandMaturity: string;
}

export function DurationStep() {
  const { formData, updateField } = useFormStore();
  const { goToPreviousStep, goToNextStep, canGoBack } = useNavigation();
  
  const {
    handleSubmit,
    setValue,
    watch,
    formState: { isValid },
    trigger
  } = useForm<DurationFormData>({
    resolver: zodResolver(durationStepSchema),
    defaultValues: {
      duration: formData.duration || '',
      demandMaturity: formData.demandMaturity || ''
    },
    mode: 'onChange'
  });

  const watchedDuration = watch('duration');
  const watchedDemandMaturity = watch('demandMaturity');

  // Récupérer le montant depuis l'étape précédente
  const amount = formData.amount || 0;

  // Obtenir les durées disponibles en fonction du montant
  const availableDurations = getAvailableDurations(amount);
  const durationOptions = availableDurations.map(d => ({
    value: d.value,
    label: d.label
  }));

  // Vérifier si la durée actuellement sélectionnée est toujours valide
  useEffect(() => {
    if (watchedDuration) {
      const isDurationStillValid = availableDurations.some(d => d.value === watchedDuration);
      if (!isDurationStillValid) {
        // Réinitialiser la durée si elle n'est plus valide
        setValue('duration', '', { shouldValidate: false });
        updateField('duration', '');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [amount, watchedDuration, availableDurations]); // setValue et updateField retirés pour éviter boucle infinie


  const demandMaturityOptions = [
    { value: '01', label: 'Immédiatement' },
    { value: '02', label: 'Dans un délai d\'un mois' },
    { value: '03', label: 'Dans plus d\'un mois' },
    { value: '05', label: 'Autre / Non défini' }
  ];
  
  const onSubmit = (data: DurationFormData) => {
    updateField('duration', data.duration);
    updateField('demandMaturity', data.demandMaturity);
    goToNextStep();
  };
  
  const handleDurationChange = async (value: string) => {
    setValue('duration', value, { shouldValidate: true });
    updateField('duration', value);
    await trigger('duration'); // Force validation
  };

  const handleDemandMaturityChange = async (value: string) => {
    setValue('demandMaturity', value, { shouldValidate: true });
    updateField('demandMaturity', value);
    await trigger('demandMaturity');
  };

  return (
    <div className="max-w-2xl mx-auto">
      <Card>
        <CardHeader>
          <CardTitle>Durée du crédit</CardTitle>
          <CardDescription>
            Durée souhaitée du remboursement ? Plus la durée est longue, moins la mensualité sera élevée.
          </CardDescription>
        </CardHeader>
        
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div>
              <RadioGroup
                label="Choisissez la durée de remboursement"
                options={durationOptions}
                value={watchedDuration}
                onChange={handleDurationChange}
                name="duration"
                required
              />
            </div>


            <div>
              <RadioGroup
                label="Quand souhaitez-vous recevoir les fonds ?"
                options={demandMaturityOptions}
                value={watchedDemandMaturity}
                onChange={handleDemandMaturityChange}
                name="demandMaturity"
                required
              />
            </div>

            <FormNavigation
              onPrevious={goToPreviousStep}
              onNext={handleSubmit(onSubmit)}
              canGoBack={canGoBack}
              canGoNext={isValid && !!watchedDuration && !!watchedDemandMaturity}
              nextButtonText="Continuer"
            />
          </form>
        </CardContent>
      </Card>
    </div>
  );
}